import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { guestApi } from "../utils/api";
import { MapPin, Flag, Clock, Truck, RefreshCw, CheckCircle2, XCircle } from "lucide-react";

function fmtDate(iso) {
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}
function fmtTime(iso) {
  return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
}
function timeAgo(iso) {
  if (!iso) return null;
  const diff  = Date.now() - new Date(iso).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (mins  < 1)  return "just now";
  if (mins  < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}
function fmtExpiry(iso) {
  if (!iso) return "Never expires";
  const d = new Date(iso);
  if (d < new Date()) return "Expired";
  return `Expires ${fmtDate(iso)} ${fmtTime(iso)}`;
}

const STEPS = [
  { key: "planned",     label: "Planned"    },
  { key: "in_progress", label: "In Transit" },
  { key: "completed",   label: "Delivered"  },
];

function ErrorScreen({ icon, title, message }) {
  return (
    <div style={{
      minHeight: "100vh", background: "#F8FAFC",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center", padding: 24,
    }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>{icon}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: "#111827", marginBottom: 8 }}>{title}</div>
      <div style={{
        background: "#FEF2F2", border: "1px solid #FECACA",
        borderRadius: 10, padding: "12px 20px", marginBottom: 24,
        fontSize: 14, color: "#DC2626", maxWidth: 360, textAlign: "center", lineHeight: 1.6,
      }}>
        {message}
      </div>
      <a href="/" style={{ background: "#0F172A", color: "#fff", textDecoration: "none", borderRadius: 10, padding: "10px 24px", fontSize: 14, fontWeight: 600 }}>
        Open LorryKhojo
      </a>
    </div>
  );
}

function Stepper({ status }) {
  const cancelled = status === "cancelled";
  const activeIdx = STEPS.findIndex(s => s.key === status);

  return (
    <div style={{ display: "flex", alignItems: "center", padding: "8px 0" }}>
      {STEPS.map((step, i) => {
        const done   = !cancelled && activeIdx >= i;
        const isLast = i === STEPS.length - 1;
        return (
          <div key={step.key} style={{ display: "flex", alignItems: "center", flex: isLast ? "0 0 auto" : 1 }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
              <div style={{
                width: 30, height: 30, borderRadius: "50%",
                background: done ? "#4F46E5" : "#F3F4F6",
                border: done ? "none" : "2px solid #E5E7EB",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                {done ? <CheckCircle2 size={16} color="#fff" /> : <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#D1D5DB" }} />}
              </div>
              <span style={{ fontSize: 11.5, fontWeight: 600, color: done ? "#111827" : "#9CA3AF", whiteSpace: "nowrap" }}>{step.label}</span>
            </div>
            {!isLast && (
              <div style={{ flex: 1, height: 2, background: done && activeIdx > i ? "#4F46E5" : "#E5E7EB", margin: "0 8px", marginBottom: 18 }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function TripGuestView() {
  const { token } = useParams();
  const [state, setState] = useState("loading"); // loading | ok | invalid | expired | offline
  const [data, setData]   = useState(null);
  const [lastFetch, setLastFetch] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const intervalRef = useRef(null);

  async function fetchData(isManual = false) {
    if (isManual) setRefreshing(true);
    try {
      const res = await guestApi.get(`/api/guest/trip/${token}`);
      setData(res.data);
      setState("ok");
      setLastFetch(Date.now());
    } catch (err) {
      const status = err.response?.status;
      if (status === 404) setState("invalid");
      else if (status === 410) setState("expired");
      else if (state !== "ok") setState("offline");
    } finally {
      if (isManual) setRefreshing(false);
    }
  }

  useEffect(() => {
    fetchData();
    intervalRef.current = setInterval(() => fetchData(), 60 * 1000);
    return () => clearInterval(intervalRef.current);
  }, [token]); // eslint-disable-line

  if (state === "loading") {
    return (
      <div style={{ minHeight: "100vh", background: "#F8FAFC", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 12 }}>
        <div style={{ width: 32, height: 32, border: "3px solid #E5E7EB", borderTopColor: "#4F46E5", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
        <div style={{ fontSize: 14, color: "#9CA3AF" }}>Loading shipment status…</div>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  if (state === "invalid") return <ErrorScreen icon="🔗" title="Link Unavailable" message="This share link is invalid or has been revoked by the sender." />;
  if (state === "expired") return <ErrorScreen icon="⏰" title="Link Expired" message="This share link has expired. Ask the sender for a new one." />;
  if (state === "offline" && !data) return <ErrorScreen icon="📡" title="Connection Error" message="Could not reach the tracking server. Please check your internet connection and try again." />;

  const { trip, share } = data;
  const cancelled = trip.status === "cancelled";
  const freshness = timeAgo(trip.lastVehicleUpdate);

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh", background: "#F8FAFC", fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif" }}>
      <style>{`* { box-sizing: border-box; }`}</style>

      <nav style={{ background: "#0F172A", padding: "0 20px", height: 52, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 28, height: 28, borderRadius: 7, background: "#4F46E5", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Truck size={14} color="#fff" />
          </div>
          <span style={{ fontSize: 16, fontWeight: 700, color: "#F1F5F9", letterSpacing: -0.3 }}>LorryKhojo</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ background: "rgba(79,70,229,0.2)", border: "1px solid rgba(99,102,241,0.4)", borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 600, color: "#A5B4FC", letterSpacing: 0.3 }}>SHIPMENT TRACKING</div>
          <div style={{ fontSize: 11, color: "#475569" }}>{fmtExpiry(share.expiresAt)}</div>
        </div>
      </nav>

      <div style={{ flex: 1, padding: "24px 20px", display: "flex", justifyContent: "center" }}>
        <div style={{ width: "100%", maxWidth: 520, display: "flex", flexDirection: "column", gap: 16 }}>

          <div style={{ background: "#fff", border: "1px solid #F0F1F3", borderRadius: 14, padding: "20px" }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#111827", marginBottom: 2 }}>
              {trip.label || `${trip.vehicleNickname || trip.vehiclePlateNumber}`}
            </div>
            <div style={{ fontSize: 12.5, color: "#9CA3AF", marginBottom: 18 }}>
              {trip.vehiclePlateNumber}
            </div>

            {cancelled ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 10, padding: "12px 14px" }}>
                <XCircle size={18} color="#9CA3AF" />
                <span style={{ fontSize: 13.5, fontWeight: 600, color: "#6B7280" }}>This trip was cancelled.</span>
              </div>
            ) : (
              <Stepper status={trip.status} />
            )}
          </div>

          <div style={{ background: "#fff", border: "1px solid #F0F1F3", borderRadius: 14, padding: "18px 20px", display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
              <MapPin size={15} color="#16A34A" style={{ marginTop: 2, flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.5 }}>Source</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>{trip.source.label || "Not labeled"}</div>
                {trip.startedAt && (
                  <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2 }}>
                    Departed {fmtDate(trip.startedAt)} · {fmtTime(trip.startedAt)}
                    {trip.startedTollPlaza ? ` · ${trip.startedTollPlaza}` : ""}
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
              <Flag size={15} color="#DC2626" style={{ marginTop: 2, flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.5 }}>Destination</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>{trip.destination.label || "Not labeled"}</div>
                {trip.completedAt && (
                  <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2 }}>
                    Arrived {fmtDate(trip.completedAt)} · {fmtTime(trip.completedAt)}
                    {trip.completedTollPlaza ? ` · ${trip.completedTollPlaza}` : ""}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
            background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 10, padding: "10px 14px",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Clock size={13} color="#D97706" />
              <span style={{ fontSize: 12.5, color: "#92400E" }}>
                {freshness ? `Status as of ${freshness} — not real-time GPS` : "No tracking data yet for this vehicle"}
              </span>
            </div>
            <button
              onClick={() => fetchData(true)}
              disabled={refreshing}
              style={{
                display: "flex", alignItems: "center", gap: 5,
                background: "none", border: "1px solid #FDE68A", borderRadius: 7,
                padding: "5px 10px", fontSize: 11.5, fontWeight: 600, color: "#92400E",
                cursor: refreshing ? "not-allowed" : "pointer", flexShrink: 0,
              }}
            >
              <RefreshCw size={11} style={{ animation: refreshing ? "spin 0.7s linear infinite" : "none" }} />
              {refreshing ? "Refreshing…" : "Refresh"}
            </button>
          </div>
        </div>
      </div>

      <div style={{ background: "#fff", borderTop: "1px solid #E5E7EB", padding: "12px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0, flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontSize: 12, color: "#9CA3AF" }}>This is a read-only shared view. You cannot modify any data.</div>
        <a href="/" style={{ fontSize: 12, fontWeight: 600, color: "#4F46E5", textDecoration: "none", display: "flex", alignItems: "center", gap: 4 }}>
          <Truck size={11} /> Track your own fleet on LorryKhojo →
        </a>
      </div>
    </div>
  );
}
